import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { type ReactNode, createContext, useEffect, useState } from 'react';

import { DINER_EMAIL_COOKIE_KEY } from 'reservations/base/constants/cookies';
import { useSuccessSnackbar } from 'reservations/base/hooks/useSnackbar';
import {
  INCORRECT_CODE_ERROR_MESSAGE,
  INCORRECT_CODE_ERROR_TYPE,
  RESEND_CODE_ERROR_MESSAGE,
  RESEND_CODE_ERROR_TYPE
} from 'reservations/dinerAccount/constants';
import {
  BeginDinerAuthResponse,
  useBeginDinerAuthQuery
} from 'reservations/dinerAccount/queries/beginDinerAuth';
import { useDinerLoginQuery } from 'reservations/dinerAccount/queries/dinerLogin';
import {
  DinerLoginResponse,
  type PaymentMethodListItem,
  useGetCurrentUserQuery
} from 'reservations/dinerAccount/queries/getCurrentUser';
import { RequestError } from 'utility/restClient';

export interface DinerAccountContextInterface {
  dinerEmail: string;
  setBeginAuthParams: (email: string, sendVerificationCode?: boolean) => void;
  hasDinerCookie: boolean;
  dinerAccountExists: boolean;
  isDinerAccountAuthenticated: boolean;
  lastThreeDigitsOfPhone: string;
  verificationCodeErrorMessage: string;
  submitVerificationCode: (verificationCode: string) => void;
  hasPaymentMethods: boolean;
  paymentMethods: Array<PaymentMethodListItem>;
  setPaymentMethods: (paymentMethods: Array<PaymentMethodListItem>) => void;
  formattedDinerPhone: string;
  dinerPhone: string;
  dinerName: string;
  isSmsAuthDisabled: boolean;
  lastBookingOptInChecklessPayments: boolean;
  lastBookingTipPercentage: number;
  shouldAutoEnroll: boolean;
  setShouldAutoEnroll: (shouldAutoEnroll: boolean) => void;
}

export const DinerAccountContext =
  createContext<DinerAccountContextInterface | null>(null);

type DinerAccountProviderProps = {
  children?: ReactNode;
};

export function DinerAccountProvider({ children }: DinerAccountProviderProps) {
  const enqueueSuccessSnackbar = useSuccessSnackbar();
  const [dinerEmailFromForm, setDinerEmailFromForm] = useState('');
  const [hasPaymentMethods, setHasPaymentMethods] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [shouldAutoEnroll, setShouldAutoEnroll] = useState(false);
  const queryClient = useQueryClient();
  const [paymentMethods, setPaymentMethods] = useState(
    Array<PaymentMethodListItem>
  );
  const [sendVerificationSms, setSendVerificationSms] =
    useState<boolean>(false);

  const dinerEmailFromCookie = Cookies.get(DINER_EMAIL_COOKIE_KEY) || '';
  const getCurrentUserResponse = useGetCurrentUserQuery({
    dinerEmail: dinerEmailFromCookie
  });

  const beginDinerAuthResponse = useBeginDinerAuthQuery({
    dinerEmail: dinerEmailFromForm,
    sendVerificationCode: sendVerificationSms
  });

  const dinerLoginResponse = useDinerLoginQuery({
    code: verificationCode,
    email: dinerEmailFromForm
  });

  const lastThreeDigitsOfPhone =
    beginDinerAuthResponse.data?.phone_last_3 || '';
  const dinerAccountData =
    getCurrentUserResponse.data || dinerLoginResponse.data;
  const isDinerAccountAuthenticated = !!dinerAccountData?.email;
  const dinerAccountExists =
    isDinerAccountAuthenticated || !!lastThreeDigitsOfPhone;

  const setBeginAuthParams = (email: string, sendVerification = true) => {
    queryClient.invalidateQueries({
      queryKey: ['dinerAuth', dinerEmailFromForm, verificationCode]
    });

    setSendVerificationSms(sendVerification);
    setDinerEmailFromForm(email);
  };

  // TODO: remove ignore next once we have more dynamic testing for diner account logic
  // istanbul ignore next
  useEffect(() => {
    if (beginDinerAuthResponse.data?.has_saved_payment_methods) {
      setHasPaymentMethods(
        beginDinerAuthResponse.data?.has_saved_payment_methods
      );
    }
  }, [beginDinerAuthResponse.data?.has_saved_payment_methods]);

  // display the information retrieved snackbar after successful OTP login
  useEffect(() => {
    if (dinerLoginResponse?.data?.email) {
      enqueueSuccessSnackbar(
        'DINER_INFORMATION_RETRIEVED_SNACKBAR',
        'Information Retrieved!'
      );
    }
  }, [dinerLoginResponse?.data?.email]);

  useEffect(() => {
    if (dinerAccountData?.payment_methods) {
      setPaymentMethods(dinerAccountData.payment_methods);
      setHasPaymentMethods(dinerAccountData.payment_methods.length > 0);
    }
  }, [dinerAccountData?.payment_methods]);

  const isSmsAuthDisabled = getIsSmsAuthDisabled(
    beginDinerAuthResponse,
    dinerLoginResponse,
    getCurrentUserResponse
  );

  const verificationCodeErrorMessage =
    getVerificationCodeErrorMessage(dinerLoginResponse);

  const contextData = {
    dinerEmail: dinerAccountData?.email ?? dinerEmailFromForm,
    setBeginAuthParams,
    dinerAccountExists,
    hasDinerCookie: !!dinerEmailFromCookie,
    isDinerAccountAuthenticated,
    lastThreeDigitsOfPhone,
    verificationCodeErrorMessage,
    submitVerificationCode: setVerificationCode,
    hasPaymentMethods,
    paymentMethods,
    setPaymentMethods,
    formattedDinerPhone: dinerAccountData?.formatted_phone_number ?? '',
    dinerPhone: dinerAccountData?.phone_number ?? '',
    dinerName: dinerAccountData?.name ?? '',
    isSmsAuthDisabled,
    lastBookingOptInChecklessPayments:
      dinerAccountData?.last_booking_opt_in_checkless_payments ?? false,
    lastBookingTipPercentage:
      dinerAccountData?.last_booking_tip_percentage ?? 0,
    shouldAutoEnroll,
    setShouldAutoEnroll
  };

  return (
    <DinerAccountContext.Provider value={contextData}>
      {children}
    </DinerAccountContext.Provider>
  );
}

type QueryResponse<T> = UseQueryResult<T, RequestError>;
/**
 * SMS auth is disabled if:
 * - Begin auth request returns 500 or
 * - Login request returns 500 or
 * - Current user request returns an error response code that is not 403
 */
const getIsSmsAuthDisabled = (
  beginDinerAuthResponse: QueryResponse<BeginDinerAuthResponse>,
  dinerLoginResponse: QueryResponse<DinerLoginResponse>,
  getCurrentUserResponse: QueryResponse<DinerLoginResponse>
): boolean =>
  beginDinerAuthResponse?.error?.code === 500 ||
  dinerLoginResponse.error?.code === 500 ||
  (!!getCurrentUserResponse?.error?.code &&
    getCurrentUserResponse?.error?.code !== 403);

const getVerificationCodeErrorMessage = (
  dinerLoginResponse: QueryResponse<DinerLoginResponse>
): string => {
  const errorType =
    dinerLoginResponse?.error?.data?.response?.data?.error_type ?? '';
  switch (errorType) {
    case INCORRECT_CODE_ERROR_TYPE:
      return INCORRECT_CODE_ERROR_MESSAGE;
    case RESEND_CODE_ERROR_TYPE:
      return RESEND_CODE_ERROR_MESSAGE;
    default:
      return dinerLoginResponse.error?.data?.response?.data?.detail ?? '';
  }
};

export default DinerAccountProvider;
